import { useEffect, useState } from 'react';

/**
 * PAIGE (docs/engineering/workbench-port.md): the export a host keeps itself. The desktop's export
 * scenes read its own export settings and record (`desktop-export`); Paige sends each confirmed
 * document to the job's destinations as soon as it is packaged, and says so through its bridge
 * (`window.paigeWorkbench.exportFacts`). The same scenes then show: Set up export while nothing but
 * Download is there, Sends as soon as ready with today's count once something is, and the delivery
 * problem when deliveries failed for good. On the desktop there is no bridge and nothing changes.
 */
export type HostExportFacts = {
  destinations: number;
  sentToday: number;
  lastSentAt: string | null;
  failed: number;
};

// PAIGE: the scene's shape, kept from CapturePoint's empty-list export scenes (removed there 2026-10-31, 62ba97da).
/**
 * What the empty Needs review list shows next: one scene for the job's export state,
 * with the real numbers it needs. Every input is something the engine reports (export
 * settings, the destination binding, the connection's last failure, Export now's count,
 * today's export history); nothing here invents or counts down a number.
 */
export type ExportNextStep = {
  kind:
    | 'plain'
    | 'not-set-up'
    | 'off'
    | 'paused'
    | 'attention'
    | 'manual'
    | 'daily'
    | 'automatic'
    | 'caught-up';
  /** Documents Export now would send (the engine's export preview); null while unknown. */
  ready: number | null;
  sentToday: number;
  lastSentAt: string | null;
  needsAttention: number;
  /** A delivered export of this job today, for Open folder; null when none. */
  latestExportId: string | null;
  destinationKind: 'filesystem' | 'content-central';
  nextRunAt: Date | null;
  /** 0–1: how much of the day before the next daily run has passed (the Daily ring). */
  dayFraction: number;
  /**
   * Why export is paused: a Content Central connection failed or its catalog binding needs a
   * look, or an automatic export could not write to its folder.
   */
  pausedBy: 'connection' | 'catalog' | 'folder' | null;
  /** The engine's sentence for a folder the automatic export could not use; null otherwise. */
  pausedMessage: string | null;
};

const REREAD_EVERY_MS = 30_000;

/** The host's export facts while `active`; null on the desktop, before the first answer, or without a job. */
export function useHostExportFacts(jobId: string, active: boolean): HostExportFacts | null {
  const [facts, setFacts] = useState<HostExportFacts | null>(null);
  useEffect(() => {
    const read = typeof window === 'undefined' ? undefined : window.paigeWorkbench?.exportFacts;
    if (!active || !read) return;
    let live = true;
    const look = () =>
      void read()
        .then((next) => {
          if (live) setFacts(next);
        })
        .catch(() => undefined);
    look();
    const timer = window.setInterval(look, REREAD_EVERY_MS);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [jobId, active]);
  return facts;
}

/**
 * The scene for those facts; `ready` is what the first-export card counts (no review needed, or confirmed). Paige
 * sends as soon as ready to every destination besides Download, so these are CapturePoint's rules for a continuous
 * export: Set up export while nothing is there and something is ready, the delivery problem when one failed for good,
 * else Sends as soon as ready.
 */
export function hostExportStep(facts: HostExportFacts, ready: number): ExportNextStep {
  const sending = facts.destinations > 0;
  const kind: ExportNextStep['kind'] = !sending
    ? ready > 0
      ? 'not-set-up'
      : 'plain'
    : facts.failed > 0
      ? 'attention'
      : 'automatic';
  return {
    kind,
    ready,
    sentToday: facts.sentToday,
    lastSentAt: facts.lastSentAt,
    needsAttention: facts.failed,
    latestExportId: null,
    destinationKind: 'filesystem',
    nextRunAt: null,
    dayFraction: 0,
    pausedBy: null,
    pausedMessage: null,
  };
}

/**
 * The job door's Export tile, which the desktop fills from its export summary (`getJobWorkSummary`,
 * which Paige refuses: paige/not-carried.ts) and so was always blank in Paige. Paige says where the
 * job sends documents: its destinations by their own labels, or Download only while Download,
 * which every job has, is the one.
 */
export function exportPlacesWords(labels: readonly string[]): string {
  if (!labels.length) return 'Download only';
  const first = labels[0]!.trim() || 'A destination';
  return labels.length === 1 ? first : `${first} + ${labels.length - 1} more`;
}

/** The tile's words from the host's destinations; null on the desktop, which has no bridge. */
export async function hostExportPlaces(): Promise<string | null> {
  const list =
    typeof window === 'undefined' ? undefined : window.paigeWorkbench?.destinations?.list;
  if (!list) return null;
  const places = (await list()).filter((destination) => destination.kind !== 'download');
  return exportPlacesWords(places.map((destination) => destination.label));
}
